import { createFileRoute } from "@tanstack/react-router";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useContext, useEffect, useState } from "react";
import { ConfigContext, OrderDataContext, ReturnOrderContext } from "@/Layout";
import ImageWithFallback from "@/components/ImageWithFallback/ImageWithFallback";
import NextButton from "@/components/NextButton/NextButton";
import { ReturnContainer } from "@/components/ReturnContainer/ReturnContainer";
import { ReturnItemComponent } from "@/components/ReturnItemComponent/ReturnItemComponent";
import { getNextRoute } from "@/utils/utils";

export const Route = createFileRoute("/return-item/$id/return-reason")({
  component: ReturnReasonPage,
});

function ReturnReasonPage() {
  const { id: returnItemId } = useParams({
    from: "/return-item/$id/return-reason",
  });
  const { returnOrder, setReturnOrder } = useContext(ReturnOrderContext);
  const { orderData } = useContext(OrderDataContext);
  const [checkboxes, setCheckboxes] = useState<boolean[]>(
    Array(orderData?.return_reasons.length).fill(false)
  );
  const configData = useContext(ConfigContext);
  const navigate = useNavigate();

  const lineItem = orderData?.line_items.find(
    (item) => item.id === returnItemId
  );

  // The component is not re-rendered when the returnItemId changes
  // So we need to reset the checkboxes when the returnItemId changes
  useEffect(() => {
    setCheckboxes(Array(orderData?.return_reasons.length).fill(false));
  }, [returnItemId, orderData]);

  useEffect(() => {
    if (!returnOrder || !orderData) {
      navigate({
        to: "/",
        search: (prev) => prev,
      });
    }
  }, [returnOrder, orderData, navigate]);

  if (!returnOrder || !orderData) return null;

  const onCheckboxChange = (i: number) => {
    const newCheckboxes = [...checkboxes];
    newCheckboxes[i] = !newCheckboxes[i];
    for (let j = 0; j < newCheckboxes.length; j++) {
      if (j !== i) {
        newCheckboxes[j] = false;
      }
    }
    setCheckboxes(newCheckboxes);

    setReturnOrder({
      ...returnOrder,
      line_items: returnOrder?.line_items.map((item) => {
        if (item.id != returnItemId) {
          return item;
        }
        return {
          ...item,
          return_reason: orderData?.return_reasons[i].id,
        };
      }),
    });
  };
  if (!configData) return null;

  return (
    <ReturnContainer key={returnItemId}>
      <div className="sm:min-w-[400px]">
        <h1 className="font-semibold text-lg">
          {configData?.return_reason_header}
        </h1>
        <p className="text-sm mt-2 min-h-12">
          {configData.return_reason_message}
        </p>
        <div>
          <h2 className="text-base font-semibold mb-2">
            {configData.return_item_text}
          </h2>
          <hr className="mt-2 mb-1" />
          <div className="flex">
            <div className="my-2">
              <ImageWithFallback src={lineItem?.product_src} />
            </div>
            <div className="flex flex-col ml-2 gap-1 mt-2">
              <span className="text-xs font-medium">
                {" "}
                {lineItem?.product_title}{" "}
              </span>
              <span className="text-xs font-light">
                {lineItem?.variant_title}
              </span>
            </div>
          </div>
          <hr className="mt-1" />
        </div>
        <div className="grid grid-flow-row auto-rows-max gap-0 sm:gap-4 overflow-auto mt-4 mb-8">
          {orderData?.return_reasons.map((reason, index) => {
            return (
              <ReturnItemComponent
                key={lineItem?.id + reason.id.toString()}
                lineItemId={lineItem?.id ?? ""}
                reason={reason.reason}
                onCheckboxChange={onCheckboxChange}
                checked={checkboxes[index]}
                index={index}
              />
            );
          })}
        </div>
        <div className="flex justify-end mt-auto py-4">
          <NextButton
            onClick={() => {
              navigate({
                to: getNextRoute(
                  returnItemId,
                  orderData,
                  returnOrder,
                  "returnReason"
                ),
                search: (prev) => prev,
              });
            }}
            text={configData.return_reason_button_message}
            buttonColor={configData.button_color}
            buttonDisabled={!checkboxes.some((checkbox) => checkbox)}
          />
        </div>
      </div>
    </ReturnContainer>
  );
}
